import { cascadeAssigneeModes } from "components/Modal/CascadeAssigneeModal";
import { modalTypes } from "constants/types";
import { useDispatch, useSelector } from "react-redux";
import { hideModal, showModal } from "slices/app/actions/modals";
import { updateOutlineSections } from "slices/proposalOutline/outlineSlice";
import { selectProposalOutlineSections } from "slices/proposalOutline/selectors";

import { getSubsectionIds, updateSectionAssignees } from "../outlineHelpers";

const applyCascadeToAssignees = (assignees, user, isAssigning) => {
  const alreadyHasUser = assignees.some(
    (assignee) => assignee.workspaceUserId === user.workspaceUserId
  );

  if (!isAssigning) {
    return assignees.filter(
      (assignee) => assignee.workspaceUserId !== user.workspaceUserId
    );
  }

  return alreadyHasUser ? assignees : [...assignees, user];
};

/**
 * Wires a section's assignee editor up to VT-9783's cascade-to-subsections behaviour.
 */
const useAssigneeCascade = (sectionId) => {
  const dispatch = useDispatch();
  const sections = useSelector(selectProposalOutlineSections);

  // cascadeInfo lands the parent's and every subsection's update in one dispatch,
  // rather than the subsections trailing the parent in a second one.
  const onAssigneesChange = (nextAssignees, cascadeInfo) => {
    dispatch((dispatchInner, getState) => {
      const currentSections = selectProposalOutlineSections(getState());
      let updatedSections = updateSectionAssignees(
        currentSections,
        sectionId,
        nextAssignees
      );

      if (cascadeInfo) {
        const { user, isAssigning } = cascadeInfo;
        const subsectionIds = getSubsectionIds(currentSections, sectionId);
        updatedSections = subsectionIds.reduce((acc, subsectionId) => {
          const subsection = acc.find((section) => section.id === subsectionId);
          const nextSubsectionAssignees = applyCascadeToAssignees(
            subsection.assignees,
            user,
            isAssigning
          );

          return updateSectionAssignees(
            acc,
            subsectionId,
            nextSubsectionAssignees
          );
        }, updatedSections);
      }

      dispatchInner(updateOutlineSections(updatedSections));
    });
  };

  const promptCascadeDecision = (closePicker, modalProps) => {
    // Close the picker dropdown so it doesn't linger behind the modal.
    closePicker();

    return new Promise((resolve) => {
      const close = () =>
        dispatch(hideModal(modalTypes.CASCADE_ASSIGNEE_MODAL));
      dispatch(
        showModal(modalTypes.CASCADE_ASSIGNEE_MODAL, {
          ...modalProps,
          onConfirm: (cascade) => {
            close();
            resolve({ proceed: true, cascade });
          },
          onCancel: () => {
            close();
            resolve({ proceed: false, cascade: false });
          },
        })
      );
    });
  };

  const onBeforeCommit = (user, isAssigning, closePicker) => {
    const subsectionIds = getSubsectionIds(sections, sectionId);
    if (subsectionIds.length === 0) {
      return { proceed: true, cascade: false };
    }

    const subsections = sections.filter((section) =>
      subsectionIds.includes(section.id)
    );
    const assignedToUserCount = subsections.filter((section) =>
      section.assignees.some(
        (assignee) => assignee.workspaceUserId === user.workspaceUserId
      )
    ).length;

    if (isAssigning) {
      return promptCascadeDecision(closePicker, {
        mode: cascadeAssigneeModes.assign,
        subsectionCount: subsectionIds.length,
        secondaryCount: assignedToUserCount,
      });
    }

    // Unassign only prompts when at least one subsection currently has this user.
    if (assignedToUserCount === 0) {
      return { proceed: true, cascade: false };
    }

    return promptCascadeDecision(closePicker, {
      mode: cascadeAssigneeModes.unassign,
      subsectionCount: subsectionIds.length,
      secondaryCount: assignedToUserCount,
    });
  };

  return { onAssigneesChange, onBeforeCommit };
};

export default useAssigneeCascade;
